import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { Customer, GetMeOutput, TrackVisitOutput, TrackVisitInput } from '@meso/types';
import { apiCall } from '../lib/api';
import { getIdToken } from '../lib/liff';
import { getLiffExtraContext } from '../lib/liff-context';
import { trackUserLogin } from '../lib/analytics';
import type { Attribution } from './useAttribution';

// Module-level guard so user_login fires at most once per page load
let userLoginFired = false;

export function useTrackVisit() {
  return useMutation<TrackVisitOutput, Error, Attribution & { page?: string }>({
    mutationFn: async ({ channel, campaign, variant, referrer, page }) => {
      const idToken = getIdToken();
      if (!idToken) throw new Error('No idToken');
      const ctx = await getLiffExtraContext();
      const body: TrackVisitInput = {
        idToken,
        channel: channel || 'direct',
        campaign: campaign ?? undefined,
        variant: variant ?? undefined,
        referrer: referrer ?? undefined,
        page,
        language: ctx.language ?? undefined,
        os: ctx.os ?? undefined,
        friend_status: ctx.friend_status,
      };
      return apiCall<TrackVisitOutput>('post', '/track/visit', body);
    },
  });
}

export function useMe() {
  const q = useQuery<Customer | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const data = await apiCall<GetMeOutput>('get', '/customers/me');
        return data.customer;
      } catch (err) {
        const msg = (err as Error).message;
        if (msg.includes('สมัคร') || msg.includes('NOT_FOUND')) return null;
        throw err;
      }
    },
  });

  // Fire DataLayer user_login once per page-load when customer info is ready
  useEffect(() => {
    const c = q.data;
    if (!c || userLoginFired) return;
    userLoginFired = true;
    trackUserLogin({
      user_id:        String(c.id ?? c.line_user_id ?? ''),
      line_user_id:   c.line_user_id ?? '',
      user_status:    'returning',
      budget_range:   (c as any).budget_range ?? null,
      has_purchased:  false,           // TODO: derive from orders if needed
      lifetime_value: 0,
      member_tier:    c.tier ?? 'Crystal',
      phone:          c.phone ?? undefined,
      email:          c.email ?? undefined,
    });
  }, [q.data]);

  return q;
}

export function useInvalidateMe() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['me'] });
}
